/**
 * The tokens of template text. A token is a braced expression, and the
 * expressions a token may hold follow SICP's evaluator (§4.1.2), written with
 * the parenthesized call syntax people already read:
 *
 *   variable         a name, such as `{_title}` or a field id: the
 *                    value it is bound to
 *   self-evaluating  a number or `true`/`false`, which stands for itself —
 *                    only ever an operand, since a token prints text
 *   application      a procedure name followed by its operands in
 *                    parentheses, separated by commas:
 *                    `{truncate(_body, 150, true)}`. An operand is any
 *                    expression, so calls nest:
 *                    `{ensureFullStop(truncate(_body, 150, false))}`.
 *
 * `read` turns a token's source into one of these expressions; evaluating it
 * is `ExpressionLogic`'s, and the procedures it can apply are the primitive
 * procedures (`PROCEDURE_DEFINITIONS` in `primitive.ts`).
 */

import type { Expression } from './expression';

// The source between the braces: a name, optionally followed by a
// parenthesized operand list. The operands are checked by `read`, not here,
// so a malformed call still reads as a token and is reported rather than
// printed as literal braces.
export const TOKEN_RE = /\{([_a-zA-Z][a-zA-Z0-9_]*(?:\s*\([^{}]*\))?)\}/g;

/** What `read` makes of a token's source: its expression, or why it has none. */
export type Reading =
  | { readonly ok: true; readonly expression: Expression }
  | { readonly ok: false; readonly reason: string };

// One lexeme per match: a name, a number, or a punctuation mark. Anything
// else (a quote, a stray symbol) matches nothing and is reported.
const LEXEME_RE = /\s*(?:([_a-zA-Z][a-zA-Z0-9_]*)|(-?\d+)|([(),]))/y;

type Lexeme =
  | { readonly kind: 'name'; readonly text: string }
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'punct'; readonly text: '(' | ')' | ',' };

function lex(source: string): readonly Lexeme[] {
  const lexemes: Lexeme[] = [];
  const re = new RegExp(LEXEME_RE);
  while (re.lastIndex < source.length) {
    if (/^\s*$/.test(source.slice(re.lastIndex))) break;
    const at = re.lastIndex;
    const match = re.exec(source);
    if (!match) {
      throw new SyntaxError(`unexpected "${source.slice(at).trim()[0]}"`);
    }
    if (match[1] !== undefined) lexemes.push({ kind: 'name', text: match[1] });
    else if (match[2] !== undefined)
      lexemes.push({ kind: 'number', value: Number(match[2]) });
    else lexemes.push({ kind: 'punct', text: match[3] as '(' | ')' | ',' });
  }
  return lexemes;
}

/** `source` read as one expression, or the reason it is not one. */
function read(source: string): Reading {
  try {
    const lexemes = lex(source);
    let at = 0;
    const next = (): Lexeme | undefined => lexemes[at++];
    const expect = (text: '(' | ')' | ','): void => {
      const lexeme = next();
      if (lexeme?.kind !== 'punct' || lexeme.text !== text) {
        throw new SyntaxError(`expected "${text}"`);
      }
    };
    const expression = (): Expression => {
      const lexeme = next();
      if (lexeme?.kind === 'number') {
        return { kind: 'self-evaluating', value: lexeme.value };
      }
      if (lexeme?.kind !== 'name') throw new SyntaxError('expected a name');
      const following = lexemes[at];
      if (following?.kind !== 'punct' || following.text !== '(') {
        if (lexeme.text === 'true' || lexeme.text === 'false') {
          return { kind: 'self-evaluating', value: lexeme.text === 'true' };
        }
        return { kind: 'variable', name: lexeme.text };
      }
      expect('(');
      const operands: Expression[] = [];
      const closing = lexemes[at];
      if (closing?.kind === 'punct' && closing.text === ')') {
        at += 1;
      } else {
        operands.push(expression());
        for (;;) {
          const separator = next();
          if (separator?.kind === 'punct' && separator.text === ')') break;
          if (separator?.kind !== 'punct' || separator.text !== ',') {
            throw new SyntaxError('expected "," or ")"');
          }
          operands.push(expression());
        }
      }
      return { kind: 'application', operator: lexeme.text, operands };
    };
    const whole = expression();
    if (at < lexemes.length) throw new SyntaxError('unexpected text after it');
    return { ok: true, expression: whole };
  } catch (error) {
    if (error instanceof SyntaxError) {
      return { ok: false, reason: error.message };
    }
    throw error;
  }
}

export const TokenLogic = {
  read,
};
